import React, { useMemo, useState, Fragment } from 'react';
import { Database, Cloud, AlertTriangle, CheckCircle2, RefreshCw, ChevronDown, Loader2 } from 'lucide-react';
interface SyncRow {
  list: string;
  sf: number;
  five9: number;
  variance: number;
  status: 'synced' | 'warning' | 'critical';
  lastSync: string;
}
const INITIAL_DATA: SyncRow[] = [{
  list: 'Dominate',
  sf: 45210,
  five9: 45210,
  variance: 0,
  status: 'synced',
  lastSync: 'Just now'
}, {
  list: 'VA Cash Out',
  sf: 18450,
  five9: 18448,
  variance: -2,
  status: 'warning',
  lastSync: '2m ago'
}, {
  list: 'Rate Watch',
  sf: 32100,
  five9: 31950,
  variance: -150,
  status: 'critical',
  lastSync: '5m ago'
}, {
  list: 'Shark Tank',
  sf: 8900,
  five9: 8900,
  variance: 0,
  status: 'synced',
  lastSync: '1m ago'
}, {
  list: 'Low FICO',
  sf: 12400,
  five9: 12400,
  variance: 0,
  status: 'synced',
  lastSync: 'Just now'
}];
const MISMATCH_SAMPLES: Record<string, {
  id: string;
  reason: string;
}[]> = {
  'VA Cash Out': [{
    id: 'LD-89421',
    reason: 'Present in SF, missing in Five9 push queue'
  }, {
    id: 'LD-89427',
    reason: 'Stale Five9 status — last touched 4h ago'
  }],
  'Rate Watch': [{
    id: 'LD-77104',
    reason: 'Five9 contains 150 leads not in SF list'
  }, {
    id: 'LD-77152',
    reason: 'Sync job partial failure at 09:14 AM'
  }, {
    id: 'LD-77198',
    reason: 'DNC flag mismatch between systems'
  }]
};
export function SyncValidation() {
  const [data, setData] = useState<SyncRow[]>(INITIAL_DATA);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const confidence = useMemo(() => {
    const totalVariance = data.reduce((sum, r) => sum + Math.abs(r.variance), 0);
    const totalLeads = data.reduce((sum, r) => sum + r.sf, 0);
    const pct = (1 - totalVariance / totalLeads) * 100;
    return pct.toFixed(2);
  }, [data]);
  const handleForceSync = () => {
    if (syncing) return;
    setSyncing(true);
    setTimeout(() => {
      setData(prev => prev.map(r => ({
        ...r,
        // Force-sync nudges Five9 to match SF, clears small variances
        five9: Math.abs(r.variance) <= 10 ? r.sf : r.five9,
        variance: Math.abs(r.variance) <= 10 ? 0 : r.variance,
        status: Math.abs(r.variance) <= 10 ? 'synced' as const : r.status,
        lastSync: 'Just now'
      })));
      setSyncing(false);
    }, 1400);
  };
  const toggleRow = (list: string, status: SyncRow['status']) => {
    if (status === 'synced') return;
    setExpanded(cur => cur === list ? null : list);
  };
  return <div className="bg-slate-900/80 border border-slate-800/60 rounded-xl overflow-hidden flex flex-col h-full" data-id="element-311">
      <div className="p-5 border-b border-slate-800/60 flex items-center justify-between bg-slate-900 gap-3 flex-wrap" data-id="element-312">
        <div className="flex items-center gap-2" data-id="element-313">
          <div className="p-2 bg-sky-500/10 rounded-lg" data-id="element-314">
            <RefreshCw className={`w-5 h-5 text-sky-400 ${syncing ? 'animate-spin' : ''}`} data-id="element-315" />
          </div>
          <div data-id="element-316">
            <h2 className="text-lg font-semibold text-white" data-id="element-317">
              Sync Validation
            </h2>
            <p className="text-sm text-slate-400" data-id="element-318">
              Salesforce vs Five9 Reconciliation
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4" data-id="element-319">
          <div className="text-right" data-id="element-320">
            <div className="text-2xl font-bold text-white tabular-nums" data-id="element-321">
              {confidence}%
            </div>
            <div className="text-xs text-slate-500" data-id="element-322">Global Sync Confidence</div>
          </div>
          <button onClick={handleForceSync} disabled={syncing} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors border border-slate-700 flex items-center gap-2 min-w-[120px] justify-center" data-id="element-323">
            {syncing ? <>
                <Loader2 className="w-4 h-4 animate-spin" data-id="element-324" />
                Syncing...
              </> : <>Force Sync All</>}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto flex-1" data-id="element-325">
        <table className="w-full text-left text-sm" data-id="element-326">
          <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wider" data-id="element-327">
            <tr data-id="element-328">
              <th className="px-5 py-3 font-medium" data-id="element-329">Routing List</th>
              <th className="px-5 py-3 font-medium text-right" data-id="element-330">
                <div className="flex items-center justify-end gap-1.5" data-id="element-331">
                  <Cloud className="w-3.5 h-3.5" data-id="element-332" /> Salesforce
                </div>
              </th>
              <th className="px-5 py-3 font-medium text-right" data-id="element-333">
                <div className="flex items-center justify-end gap-1.5" data-id="element-334">
                  <Database className="w-3.5 h-3.5" data-id="element-335" /> Five9
                </div>
              </th>
              <th className="px-5 py-3 font-medium text-right" data-id="element-336">Variance</th>
              <th className="px-5 py-3 font-medium text-right" data-id="element-337">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60" data-id="element-338">
            {data.map(row => {
            const isExpanded = expanded === row.list;
            const isClickable = row.status !== 'synced';
            return <Fragment key={row.list} data-id="element-339">
                  <tr onClick={() => toggleRow(row.list, row.status)} className={`transition-colors group ${isClickable ? 'hover:bg-slate-800/40 cursor-pointer' : 'hover:bg-slate-800/20'}`} data-id="element-340">
                    <td className="px-5 py-3.5" data-id="element-341">
                      <div className="flex items-center gap-2" data-id="element-342">
                        {isClickable && <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-343" />}
                        <div data-id="element-344">
                          <div className="font-medium text-slate-200" data-id="element-345">
                            {row.list}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5" data-id="element-346">
                            Last sync: {row.lastSync}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-right font-medium text-slate-300 tabular-nums" data-id="element-347">
                      {row.sf.toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-right font-medium text-slate-300 tabular-nums" data-id="element-348">
                      {row.five9.toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-349">
                      {row.variance === 0 ? <span className="text-slate-500 tabular-nums" data-id="element-350">—</span> : <span className={`font-bold tabular-nums ${row.status === 'critical' ? 'text-rose-400' : 'text-amber-400'}`} data-id="element-351">
                          {row.variance > 0 ? '+' : ''}
                          {row.variance}
                        </span>}
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-352">
                      <div className="flex items-center justify-end" data-id="element-353">
                        {row.status === 'synced' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded text-xs font-medium border border-emerald-500/20" data-id="element-354">
                            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-355" /> Synced
                          </div> : row.status === 'warning' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-500/10 text-amber-400 rounded text-xs font-medium border border-amber-500/20" data-id="element-356">
                            <AlertTriangle className="w-3.5 h-3.5" data-id="element-357" /> Warning
                          </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-rose-500/10 text-rose-400 rounded text-xs font-medium border border-rose-500/20" data-id="element-358">
                            <AlertTriangle className="w-3.5 h-3.5" data-id="element-359" /> Mismatch
                          </div>}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && isClickable && <tr className="bg-slate-950/40" data-id="element-360">
                      <td colSpan={5} className="px-5 py-4" data-id="element-361">
                        <div className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wider" data-id="element-362">
                          Sample Discrepancies
                        </div>
                        <div className="space-y-2" data-id="element-363">
                          {(MISMATCH_SAMPLES[row.list] ?? []).map(s => <div key={s.id} className="flex items-start justify-between gap-3 p-2.5 bg-slate-900/60 rounded border border-slate-800" data-id="element-364">
                              <div data-id="element-365">
                                <span className="text-xs font-mono text-indigo-400" data-id="element-366">
                                  {s.id}
                                </span>
                                <p className="text-xs text-slate-300 mt-0.5" data-id="element-367">
                                  {s.reason}
                                </p>
                              </div>
                              <button className="text-xs text-slate-500 hover:text-slate-200 shrink-0" data-id="element-368">
                                Investigate →
                              </button>
                            </div>)}
                        </div>
                      </td>
                    </tr>}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}